import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { styles } from './dashboard.style.js';
import './dashboards/wo-mini-dashboard/wo-mini-dashboard.js';
import './dashboards/pm-mini-dashboard/pm-mini-dashboard.js';
import './dashboards/assets-mini-dashboard/assets-mini-dashboard.js';
import './dashboards/downtime-mini-dashboard/downtime-mini-dashboard.js';
import './dashboards/safety-mini-dashboard/safety-mini-dashboard.js';
import './dashboards/inventory-mini-dashboard/inventory-mini-dashboard.js';
import { 
  getWorkOrdersByCompany,
  getPMWorkOrdersByCompany,
  getDowntimesByCompany,
  getAssetsByCompany,
  getPreventiveMaintenanceByCompany,
  getSafetyReportsByCompany,
  getPartsByCompany,
  getCompanyById,
} from '@cmms/api';

import { authService } from '../services/auth.service.js';
import { Firestore } from 'firebase/firestore';
import { WorkOrderStatusCat, SubscriptionPlanCat } from '@cmms/models';

interface WorkOrder {
  status: string;
  title?: string;
  model?: { status_cat: WorkOrderStatusCat };
  status_cat?: WorkOrderStatusCat;
}

interface PMTask {
  id: string;
  asset: string;
  dueDate: string;
  assigned: string;
}

interface Incident {
  date: string;
  description: string;
  status: string;
}

interface Asset {
  id: string;
  name: string;
  model?: {
    name: string;
  }
}

interface Downtime {
  id: string;
  model: {
    asset_id: string;
    reason_id: string;
    started_at: number;
    ended_at: number | null;
    duration: number | null;
  }
}

interface ProcessedDowntime {
  asset_name: string;
  started_at: number;
  duration: number; // in hours
}

@customElement('cmms-dashboard')
export class CMMSDashboard extends LitElement {
  static styles = styles;
  @state()
  private isLoading = true;

  @state()
  private lastUpdated: string = '';

  @state()
  private currentPage = this.getStoredCurrentPage();

  @state()
  private expandedWidget: string | null = null;

  @property({ type: Boolean })
  sidebarCollapsed = false;

  @property({ type: String })
  private companyId: string = '';

  @property({type: Object})
  private user: any = null;

  @property({ type: Object })
  @property({ type: Object }) firestore: Firestore = null as any;
  
  @state()
  private companySubscriptionPlan: SubscriptionPlanCat = SubscriptionPlanCat.STARTER;
  
  @state()
  private dashboardData = {
    openWorkOrders: 0,
    overduePMTasks: 0,
    incidentsReported: 0,
    sparePartsLowStock: 0,
    assetsCurrentlyDown: 0,
    totalDowntimeEvents: 0,
    workOrderStatus: {
      open: 0,
      inProgress: 0,
      completed: 0
    },
    upcomingPM: [] as PMTask[],
    overduePM: [] as PMTask[],
    recentIncidents: [] as Incident[],
    recentDowntimes: [] as ProcessedDowntime[],
    weekToDateDowntime: [] as ProcessedDowntime[],
    monthToDateDowntime: [] as ProcessedDowntime[],
    assetHealth: {
      critical: 0,
      good: 0,
      idle: 0
    }
  };

  private async getAllWorkOrders() {
    try {
      const [regularWOs, pmWOs] = await Promise.all([
        getWorkOrdersByCompany(this.firestore, this.companyId),
        getPMWorkOrdersByCompany(this.firestore, this.companyId)
      ]);
      
      const regularData = regularWOs.data || [];
      const pmData = pmWOs.data || [];
      const allWorkOrders = [...regularData, ...pmData];
      
      return { success: true, data: allWorkOrders };
    } catch (error) {
      console.error('Error fetching work orders:', error);
      return { success: false, data: [], message: 'Failed to fetch work orders' };
    }
  }
  
  private async getDowntimeData() {
    try {
      const result = await getDowntimesByCompany(this.firestore, this.companyId);
      if (result.data) {
        return { success: true, data: result.data };
      } else {
        return { success: false, data: [], message: result.error || 'Failed to fetch downtime data' };
      }
    } catch (error) {
      console.error('Error fetching downtime data:', error);
      return { success: false, data: [], message: 'Failed to fetch downtime data' };
    }
  }

  private async getAssets() {
    try {
      const result = await getAssetsByCompany(this.firestore, this.companyId);
      if (result.data) {
        return { success: true, data: result.data };
      } else {
        return { success: false, data: [], message: result.error || 'Failed to fetch asset data' };
      }
    } catch (error) {
      console.error('Error fetching asset data:', error);
      return { success: false, data: [], message: 'Failed to fetch asset data' };
    }
  }

  private async getPMTasks() {
    try {
      const result = await getPreventiveMaintenanceByCompany(this.firestore, this.companyId);
      if (result.data) {
        return { success: true, data: result.data };
      } else {
        return { success: false, data: [], message: result.error || 'Failed to fetch PM tasks' };
      }
    } catch (error) {
      console.error('Error fetching PM tasks:', error);
      return { success: false, data: [], message: 'Failed to fetch PM tasks' };
    }
  }

  private async getIncidents() {
    try {
      const result = await getSafetyReportsByCompany(this.firestore, this.companyId);
      if (result.data) {
        return { success: true, data: result.data };
      } else {
        return { success: false, data: [], message: result.error || 'Failed to fetch incidents' };
      }
    } catch (error) {
      console.error('Error fetching incidents:', error);
      return { success: false, data: [], message: 'Failed to fetch incidents' };
    }
  }

  private async getParts() {
    try {
      const result = await getPartsByCompany(this.firestore, this.companyId);
      if (result.data) {
        return { success: true, data: result.data };
      } else {
        return { success: false, data: [], message: result.error || 'Failed to fetch parts' };
      }
    } catch (error) {
      console.error('Error fetching parts:', error);
      return { success: false, data: [], message: 'Failed to fetch parts' };
    }
  }

  private resetDashboardData() {
    this.dashboardData = {
      openWorkOrders: 0,
      overduePMTasks: 0,
      incidentsReported: 0,
      sparePartsLowStock: 0,
      assetsCurrentlyDown: 0,
      totalDowntimeEvents: 0,
      workOrderStatus: {
        open: 0,
        inProgress: 0,
        completed: 0
      },
      upcomingPM: [],
      overduePM: [],
      recentIncidents: [],
      recentDowntimes: [],
      weekToDateDowntime: [],
      monthToDateDowntime: [],
      assetHealth: {
        critical: 0,
        good: 0,
        idle: 0
      }
    };
  }

  connectedCallback() {
    super.connectedCallback();
    
    // Listen for navigation events from header
    window.addEventListener('navigate', (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail.page) {
        this.currentPage = customEvent.detail.page;
        this.storeCurrentPage(customEvent.detail.page);
      }
    });
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    
    if (changedProperties.has('companyId') && this.companyId) {
      this.loadDashboardData();
    }
    
    if (changedProperties.has('companyId') || changedProperties.has('firestore')) {
      this.fetchCompanySubscriptionPlan();
    }
  }

  // Fetch company subscription plan using API
  private async fetchCompanySubscriptionPlan() {
    if (!this.companyId || !this.firestore) return;
    
    console.log('Company ID:', this.companyId);
    
    try {
      const result = await getCompanyById(this.firestore, this.companyId);
      
      if (result.data && !result.error && (result.data as any).model) {
        this.companySubscriptionPlan = (result.data as any).model.subscription_plan_cat || SubscriptionPlanCat.STARTER;
        console.log('Company subscription plan:', this.companySubscriptionPlan);
        this.requestUpdate();
      } else {
        console.warn('Company not found, defaulting to STARTER plan');
        this.companySubscriptionPlan = SubscriptionPlanCat.STARTER;
        this.requestUpdate();
      }
    } catch (error) {
      console.error('Error fetching company subscription plan:', error);
      this.companySubscriptionPlan = SubscriptionPlanCat.STARTER;
    }
  }

  private async loadDashboardData() {
    try {
      this.isLoading = true;
      
      if (!this.companyId) {
        console.warn('No company ID available, skipping dashboard data load');
        this.resetDashboardData();
        return;
      }
      
      const [workOrdersResult, downtimesResult, assetsResult, pmTasksResult, incidentsResult, partsResult] = await Promise.all([
        this.getAllWorkOrders(),
        this.getDowntimeData(),
        this.getAssets(),
        this.getPMTasks(),
        this.getIncidents(),
        this.getParts()
      ]);

      // Process work orders data
      if (workOrdersResult.success) {
        const workOrders = workOrdersResult.data as WorkOrder[];
        
        if (workOrders && workOrders.length > 0) {
          const openWorkOrders = workOrders.filter((wo: WorkOrder) => 
            (wo.model?.status_cat === WorkOrderStatusCat.OPEN || wo.status_cat === WorkOrderStatusCat.OPEN)
          ).length;
          const inProgressWorkOrders = workOrders.filter((wo: WorkOrder) => 
            (wo.model?.status_cat === WorkOrderStatusCat.IN_PROGRESS || wo.status_cat === WorkOrderStatusCat.IN_PROGRESS)
          ).length;
          const completedWorkOrders = workOrders.filter((wo: WorkOrder) => 
            (wo.model?.status_cat === WorkOrderStatusCat.COMPLETED || wo.status_cat === WorkOrderStatusCat.COMPLETED)
          ).length;
          const totalActiveWorkOrders = openWorkOrders + inProgressWorkOrders;

          this.dashboardData = {
            ...this.dashboardData,
            openWorkOrders: totalActiveWorkOrders,
            workOrderStatus: {
              open: openWorkOrders,
              inProgress: inProgressWorkOrders,
              completed: completedWorkOrders
            }
          };
        } else {
          this.dashboardData = {
            ...this.dashboardData,
            openWorkOrders: 0,
            workOrderStatus: {
              open: 0,
              inProgress: 0,
              completed: 0
            }
          };
        }
      } else {
        this.dashboardData = {
          ...this.dashboardData,
          openWorkOrders: 0,
          workOrderStatus: {
            open: 0,
            inProgress: 0,
            completed: 0
          }
        };
      }

      // Process downtime data and merge with asset names
      if (downtimesResult.success && assetsResult.success) {
        const downtimes = downtimesResult.data as Downtime[];
        const assets = assetsResult.data as Asset[];

        const assetMap = new Map<string, string>();
        assets.forEach(asset => {
          const assetName = asset.model?.name || asset.name; // Use .name instead of .asset_name
          if (assetName) {
            assetMap.set(asset.id, assetName);
          }
        });

        const processedDowntimes = downtimes
          .filter(dt => dt.model.started_at && dt.model.ended_at)
          .map(dt => {
            const durationMs = dt.model.ended_at! - dt.model.started_at;
            const durationHours = Math.round((durationMs / (1000 * 60 * 60)) * 10) / 10;
            return {
              asset_name: assetMap.get(dt.model.asset_id) || 'Unknown Asset',
              started_at: dt.model.started_at,
              duration: durationHours
            };
          });

        const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
        const recent = processedDowntimes
            .filter(d => d.started_at >= sevenDaysAgo)
            .sort((a, b) => b.started_at - a.started_at)
            .slice(0, 5);

        const now = new Date();
        // Calculate start of week (Monday 12:00 AM)
        const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert Sunday (0) to 6 days back
        const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysToMonday);
        startOfWeek.setHours(0, 0, 0, 0); // Set to 12:00 AM
        const weekToDate = processedDowntimes
            .filter(d => d.started_at >= startOfWeek.getTime())
            .sort((a, b) => b.started_at - a.started_at)
            .slice(0, 5);

        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthToDate = processedDowntimes
            .filter(d => d.started_at >= startOfMonth.getTime())
            .sort((a, b) => b.started_at - a.started_at)
            .slice(0, 5);

        const activeCount = downtimes.filter(dt => dt.model.ended_at === null).length;

        this.dashboardData = {
          ...this.dashboardData,
          assetsCurrentlyDown: assets.length, // Show total assets instead of just down assets
          recentDowntimes: recent,
          weekToDateDowntime: weekToDate,
          monthToDateDowntime: monthToDate,
          totalDowntimeEvents: downtimes.length // Add total downtime events
        };
      } else {
        this.dashboardData = {
          ...this.dashboardData,
          assetsCurrentlyDown: 0,
          recentDowntimes: [],
          weekToDateDowntime: [],
          monthToDateDowntime: [],
          totalDowntimeEvents: 0
        };
      }

      // Process PM tasks data
      if (pmTasksResult.success && assetsResult.success) {
        const pmTasks = pmTasksResult.data as any[];
        const assets = assetsResult.data as Asset[];
        
        const assetMap = new Map<string, string>();
        assets.forEach(asset => {
          const assetName = asset.model?.name || asset.name;
          if (assetName) {
            assetMap.set(asset.id, assetName);
          }
        });

        // Determine date boundaries
        const now = Date.now();
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const startOfTodayMs = startOfToday.getTime();

        // Helper to compute if overdue with grace period
        const isOverdue = (pm: any) => {
          const due = pm.model?.next_due_at;
          if (!due) return false;
          const graceDays = Number(pm.model?.grace_days || 0);
          const graceMs = graceDays * 24 * 60 * 60 * 1000;
          return pm.model?.active !== false && (due + graceMs) < now;
        };

        const upcomingSorted = pmTasks
          .filter(pm => pm.model?.active && pm.model?.next_due_at && pm.model.next_due_at >= startOfTodayMs)
          .sort((a, b) => a.model.next_due_at - b.model.next_due_at);

        const overdueSorted = pmTasks
          .filter(pm => isOverdue(pm))
          .sort((a, b) => a.model.next_due_at - b.model.next_due_at);

        const upcomingPM = upcomingSorted
          .slice(0, 3)
          .map(pm => ({
            id: pm.id,
            asset: assetMap.get(pm.model.asset_id) || 'Unknown Asset',
            dueDate: this.formatDate(pm.model.next_due_at),
            assigned: pm.model.default_assigned_to || 'Unassigned'
          }));

        const overduePM = overdueSorted
          .slice(0, 5)
          .map(pm => ({
            id: pm.id,
            asset: assetMap.get(pm.model.asset_id) || 'Unknown Asset',
            dueDate: this.formatDate(pm.model.next_due_at),
            assigned: pm.model.default_assigned_to || 'Unassigned'
          }));

        this.dashboardData = {
          ...this.dashboardData,
          upcomingPM: upcomingPM,
          overduePMTasks: overdueSorted.length,
          overduePM: overduePM
        };
      }

      // Process incidents data
      if (incidentsResult.success) {
        const incidents = incidentsResult.data as any[];
        
        const recentIncidents = incidents
          .filter(incident => incident.model?.date_reported)
          .map(incident => ({
            description: incident.model.title || 'No title',
            date: this.formatDate(incident.model.date_reported),
            status: incident.model.status || 'Unknown'
          }))
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .slice(0, 3);

        this.dashboardData = {
          ...this.dashboardData,
          recentIncidents: recentIncidents,
          incidentsReported: incidents.length
        };
      }

      // Process parts data
      if (partsResult.success) {
        const parts = partsResult.data as any[];
        
        this.dashboardData = {
          ...this.dashboardData,
          sparePartsLowStock: parts.length // Show total parts instead of just low stock
        };
      } else {
        this.dashboardData = {
          ...this.dashboardData,
          sparePartsLowStock: 0
        };
      }

      this.dashboardData = {
        ...this.dashboardData,
        // Keep overdue count if we calculated it above; otherwise default to 0
        overduePMTasks: this.dashboardData.overduePMTasks || 0,
        // Keep parts count if we calculated it above; otherwise default to 0
        sparePartsLowStock: this.dashboardData.sparePartsLowStock || 0,
        assetHealth: {
          critical: 0,
          good: 0,
          idle: 0
        }
      };
      

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      this.resetDashboardData();
    } finally {
      this.isLoading = false;
      this.lastUpdated = new Date().toLocaleString();
    }
  }

  private getStoredCurrentPage(): string {
    try {
      const stored = localStorage.getItem('cmms_current_page');
      return stored || 'dashboard';
    } catch (error) {
      console.warn('Failed to get stored current page:', error);
      return 'dashboard';
    }
  }

  private storeCurrentPage(page: string): void {
    try {
      localStorage.setItem('cmms_current_page', page);
    } catch (error) {
      console.warn('Failed to store current page:', error);
    }
  }

  private handleNavClick(item: string) {
    if (['reports', 'admin'].includes(item)) {
      return;
    }
    this.currentPage = item;
    this.storeCurrentPage(item);
    
    this.dispatchEvent(new CustomEvent('navigate', {
      detail: { page: item },
      bubbles: true,
      composed: true
    }));
  }

  private formatDate(timestamp: number): string {
    const date = new Date(timestamp);
    return date.toLocaleDateString();
  }

  private openPMFromWidget(pmId: string) {
    try {
      localStorage.setItem('pm_open_id', pmId);
    } catch {}
    this.handleNavClick('preventive-maintenance');
    // Emit a global event so the PM page can react immediately if mounted
    window.dispatchEvent(new CustomEvent('open-pm-from-dashboard', { detail: { pmId } }));
  }

  private renderMainContent() {
    switch (this.currentPage) {
      case 'mini-work-orders':
        return html`<wo-mini-dashboard .companyId=${this.companyId} .firestore=${this.firestore}></wo-mini-dashboard>`;
      case 'mini-preventive-maintenance':
        return html`<pm-mini-dashboard .companyId=${this.companyId} .firestore=${this.firestore}></pm-mini-dashboard>`;
      case 'mini-assets':
        return html`<assets-mini-dashboard .companyId=${this.companyId} .firestore=${this.firestore}></assets-mini-dashboard>`;
      case 'mini-downtime':
        return html`<downtime-mini-dashboard .companyId=${this.companyId} .firestore=${this.firestore}></downtime-mini-dashboard>`;
      case 'mini-incidents':
        return html`<safety-mini-dashboard .companyId=${this.companyId} .firestore=${this.firestore}></safety-mini-dashboard>`;
      case 'mini-inventory':
        return html`<inventory-mini-dashboard .companyId=${this.companyId} .firestore=${this.firestore}></inventory-mini-dashboard>`;
      case 'work-orders':
        return html`
          <work-orders-page
            .companyId=${this.companyId}
            .firestore=${this.firestore}
          ></work-orders-page>
        `;
      case 'assets':
        return html`
          <assets-page .companyId=${this.companyId} .firestore=${this.firestore}></assets-page>
        `;
      case 'inventory':
        return html`
          <parts-page 
            .companyId=${this.companyId} 
            .firestore=${this.firestore}
            .subscriptionPlan=${this.companySubscriptionPlan}
          ></parts-page>
        `;
      case 'locations':
        return html`
          <locations-page 
            .companyId=${this.companyId} 
            .firestore=${this.firestore}
            .subscriptionPlan=${this.companySubscriptionPlan}
          ></locations-page>
        `;
      case 'preventive-maintenance':
        return html`
          <pm-page 
            .companyId=${this.companyId} 
            .firestore=${this.firestore}
            .subscriptionPlan=${this.companySubscriptionPlan}
          ></pm-page>
        `;
      case 'incidents':
        return html`
          <health-safety-page 
            .companyId=${this.companyId} 
            .firestore=${this.firestore}
            .subscriptionPlan=${this.companySubscriptionPlan}
          ></health-safety-page>
        `;
      case 'users':
        return html`
          <users-page 
            .companyId=${this.companyId} 
            .firestore=${this.firestore}
            .subscriptionPlan=${this.companySubscriptionPlan}
          ></users-page>
        `;
      case 'technical':
        return html`
          <technical-page 
            .companyId=${this.companyId} 
            .firestore=${this.firestore}
            .subscriptionPlan=${this.companySubscriptionPlan}
          ></technical-page>
        `;
      case 'gmp':
        return html`
          <technical-page 
            .companyId=${this.companyId} 
            .firestore=${this.firestore} 
            .subscriptionPlan=${this.companySubscriptionPlan}
            initialtab="templates"
          ></technical-page>
        `;
      case 'gmp-audit':
        return html`
          <technical-page 
            .companyId=${this.companyId} 
            .firestore=${this.firestore} 
            .subscriptionPlan=${this.companySubscriptionPlan}
            initialtab="audits"
          ></technical-page>
        `;
      case 'dashboard':
      default:
        return this.renderMainCategories();
    }
  }

  private renderDowntimeTable(downtimeData: ProcessedDowntime[], title: string) {
    return html`
      <div class="widget">
        <h3>${title}</h3>
        <div class="downtime-list">
          ${downtimeData.length > 0 ? html`
            <div class="data-table-container">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th>Date</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  ${downtimeData.map(downtime => html`
                    <tr>
                      <td>${downtime.asset_name}</td>
                      <td>${this.formatDate(downtime.started_at)}</td>
                      <td>${downtime.duration} hrs</td>
                    </tr>
                  `)}
                </tbody>
              </table>
            </div>
          ` : html`
            <div class="no-data-message">
              <sl-icon name="clock" size="large"></sl-icon>
              <p>No downtime events</p>
            </div>
          `}
        </div>
      </div>
    `;
  }

  private renderMainCategories() {
    const categories = [
      { id: 'work-orders', name: 'Work Orders', icon: 'clipboard-check', metric: this.dashboardData.openWorkOrders },
      { id: 'preventive-maintenance', name: 'Preventive Maintenance', icon: 'calendar-check', metric: this.dashboardData.overduePMTasks },
      { id: 'assets', name: 'Assets', icon: 'box-seam', metric: this.dashboardData.assetsCurrentlyDown },
      { id: 'downtime', name: 'Downtime', icon: 'clock-history', metric: this.dashboardData.totalDowntimeEvents },
      { id: 'incidents', name: 'Health & Safety', icon: 'shield-exclamation', metric: this.dashboardData.incidentsReported },
      { id: 'inventory', name: 'Inventory', icon: 'boxes', metric: this.dashboardData.sparePartsLowStock },
    ];

    return html`
      <div class="dashboard-header">
        <div class="title">
          <h2 style="margin:0; font-size:1.25rem; color: var(--sl-color-neutral-900);">Dashboard</h2>
        </div>
        <div class="actions">
          <span class="last-updated">Last updated: ${this.lastUpdated || new Date().toLocaleString()}</span>
          <sl-button size="small" @click=${() => this.loadDashboardData()} ?disabled=${this.isLoading}>
            <sl-icon name="arrow-repeat" slot="prefix"></sl-icon>
            Refresh
          </sl-button>
        </div>
      </div>
      <div class="category-grid">
        ${categories.map(cat => html`
          <div class="category-card ${this.expandedWidget === cat.id ? 'expanded' : ''}" @click=${() => this.toggleWidgetExpansion(cat.id)}>
            <sl-icon name=${cat.icon}></sl-icon>
            <div class="info">
              <h3>${cat.name}</h3>
              <p>${cat.metric ?? 0}</p>
            </div>
          </div>
        `)}
      </div>
      ${this.expandedWidget ? html`
        <div class="expanded-widget-content">
          ${this.renderExpandedWidget()}
        </div>
      ` : ''}
    `;
  }

  private toggleWidgetExpansion(categoryId: string) {
    if (this.expandedWidget === categoryId) {
      this.expandedWidget = null;
    } else {
      this.expandedWidget = categoryId;
    }
  }

  private renderExpandedWidget() {
    if (!this.expandedWidget) return html``;

    switch (this.expandedWidget) {
      case 'work-orders':
        return html`<wo-mini-dashboard .companyId=${this.companyId} .firestore=${this.firestore}></wo-mini-dashboard>`;
      case 'preventive-maintenance':
        return html`<pm-mini-dashboard .companyId=${this.companyId} .firestore=${this.firestore}></pm-mini-dashboard>`;
      case 'assets':
        return html`<assets-mini-dashboard .companyId=${this.companyId} .firestore=${this.firestore}></assets-mini-dashboard>`;
      case 'downtime':
        return html`<downtime-mini-dashboard .companyId=${this.companyId} .firestore=${this.firestore}></downtime-mini-dashboard>`;
      case 'incidents':
        return html`<safety-mini-dashboard .companyId=${this.companyId} .firestore=${this.firestore}></safety-mini-dashboard>`;
      case 'inventory':
        return html`<inventory-mini-dashboard .companyId=${this.companyId} .firestore=${this.firestore}></inventory-mini-dashboard>`;
      default:
        return html``;
    }
  }

  render() {
    if (this.isLoading) {
      return html`
        <div class="loading">
          <sl-spinner size="large"></sl-spinner>
        </div>
      `;
    }

    return html`
      <div class="dashboard-container">
        <div class="sidebar ${this.sidebarCollapsed ? 'collapsed' : ''}">
          
          ${this.sidebarCollapsed ? html`
            <sl-tooltip content="Dashboard" placement="right">
              <div class="nav-item ${this.currentPage === 'dashboard' ? 'active' : ''}" @click=${() => this.handleNavClick('dashboard')}>
                <sl-icon name="grid-3x3-gap"></sl-icon>
                <span>Dashboard</span>
              </div>
            </sl-tooltip>
            
            <sl-tooltip content="Work Orders" placement="right">
              <div class="nav-item ${this.currentPage === 'work-orders' ? 'active' : ''}" @click=${() => this.handleNavClick('work-orders')}>
                <sl-icon name="clipboard-check"></sl-icon>
                <span>Work Orders</span>
              </div>
            </sl-tooltip>
            
            <sl-tooltip content="Assets" placement="right">
              <div class="nav-item ${this.currentPage === 'assets' ? 'active' : ''}" @click=${() => this.handleNavClick('assets')}>
                <sl-icon name="box-seam"></sl-icon>
                <span>Assets</span>
              </div>
            </sl-tooltip>
            
            <sl-tooltip content="Locations" placement="right">
              <div class="nav-item ${this.currentPage === 'locations' ? 'active' : ''}" @click=${() => this.handleNavClick('locations')}>
                <sl-icon name="geo-alt"></sl-icon>
                <span>Locations</span>
              </div>
            </sl-tooltip>

            <sl-tooltip content="Inventory" placement="right">
              <div class="nav-item ${this.currentPage === 'inventory' ? 'active' : ''}" @click=${() => this.handleNavClick('inventory')}>
                <sl-icon name="boxes"></sl-icon>
                <span>Inventory</span>
              </div>
            </sl-tooltip>
            
            <sl-tooltip content="Preventive Maintenance" placement="right">
              <div class="nav-item ${this.currentPage === 'preventive-maintenance' ? 'active' : ''}" @click=${() => this.handleNavClick('preventive-maintenance')}>
                <sl-icon name="calendar-check"></sl-icon>
                <span>Preventive Maintenance</span>
              </div>
            </sl-tooltip>
            
            <sl-tooltip content="Health & Safety" placement="right">
              <div class="nav-item ${this.currentPage === 'incidents' ? 'active' : ''}" @click=${() => this.handleNavClick('incidents')}>
                <sl-icon name="shield-exclamation"></sl-icon>
                <span>Health & Safety</span>
              </div>
            </sl-tooltip>
            <sl-tooltip content="Technical" placement="right">
              <div class="nav-item ${this.currentPage === 'technical' ? 'active' : ''}" @click=${() => this.handleNavClick('technical')}>
                <sl-icon name="journal-text"></sl-icon>
                <span>Technical</span>
              </div>
            </sl-tooltip>
            ${this.user?.role === 'Admin' || this.user?.role === 'admin' ? html`
              <sl-tooltip content="User Management" placement="right">
                <div class="nav-item ${this.currentPage === 'users' ? 'active' : ''}" @click=${() => this.handleNavClick('users')}>
                  <sl-icon name="people"></sl-icon>
                  <span>Users</span>
                </div>
              </sl-tooltip>
            ` : ''}
            
            <sl-tooltip content="Reports" placement="right">
              <div class="nav-item disabled ${this.currentPage === 'reports' ? 'active' : ''}" @click=${() => this.handleNavClick('reports')}>
                <sl-icon name="bar-chart"></sl-icon>
                <span>Reports</span>
              </div>
            </sl-tooltip>
            
          ` : html`
            <div class="nav-item ${this.currentPage === 'dashboard' ? 'active' : ''}" @click=${() => this.handleNavClick('dashboard')}>
              <sl-icon name="grid-3x3-gap"></sl-icon>
              <span>Dashboard</span>
            </div>
            
            <div class="nav-item ${this.currentPage === 'work-orders' ? 'active' : ''}" @click=${() => this.handleNavClick('work-orders')}>
              <sl-icon name="clipboard-check"></sl-icon>
              <span>Work Orders</span>
            </div>
            
            <div class="nav-item ${this.currentPage === 'assets' ? 'active' : ''}" @click=${() => this.handleNavClick('assets')}>
              <sl-icon name="box-seam"></sl-icon>
              <span>Assets</span>
            </div>
            
            <div class="nav-item ${this.currentPage === 'locations' ? 'active' : ''}" @click=${() => this.handleNavClick('locations')}>
              <sl-icon name="geo-alt"></sl-icon>
              <span>Locations</span>
            </div>

            <div class="nav-item ${this.currentPage === 'inventory' ? 'active' : ''}" @click=${() => this.handleNavClick('inventory')}>
              <sl-icon name="boxes"></sl-icon>
              <span>Inventory</span>
            </div>
            
            <div class="nav-item ${this.currentPage === 'preventive-maintenance' ? 'active' : ''}" @click=${() => this.handleNavClick('preventive-maintenance')}>
              <sl-icon name="calendar-check"></sl-icon>
              <span>Preventive Maintenance</span>
            </div>
            
            <div class="nav-item ${this.currentPage === 'incidents' ? 'active' : ''}" @click=${() => this.handleNavClick('incidents')}>
              <sl-icon name="shield-exclamation"></sl-icon>
              <span>Health & Safety</span>
            </div>
            <div class="nav-item ${this.currentPage === 'technical' ? 'active' : ''}" @click=${() => this.handleNavClick('technical')}>
              <sl-icon name="journal-text"></sl-icon>
              <span>Technical</span>
            </div>
            
            ${this.user?.role === 'Admin' || this.user?.role === 'admin' ? html`
              <div class="nav-item ${this.currentPage === 'users' ? 'active' : ''}" @click=${() => this.handleNavClick('users')}>
                <sl-icon name="people"></sl-icon>
                <span>Users</span>
              </div>
            ` : ''}
            
            <div class="nav-item disabled ${this.currentPage === 'reports' ? 'active' : ''}" @click=${() => this.handleNavClick('reports')}>
              <sl-icon name="bar-chart"></sl-icon>
              <span>Reports</span>
            </div>
            
            <div class="nav-item disabled ${this.currentPage === 'admin' ? 'active' : ''}" @click=${() => this.handleNavClick('admin')}>
              <sl-icon name="sliders"></sl-icon>
              <span>Admin/ Settings</span>
            </div>
          `}
        </div>

        <div class="main-content">
          ${this.renderMainContent()}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cmms-dashboard': CMMSDashboard;
  }
}
